import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Mail, MessageSquare, Send, RefreshCw, Users, Search, Paperclip, X, Eye, Save, Plus, Trash2 } from "lucide-react";
import type { CommunicationLog, Client } from "@shared/schema";

const CHANNEL_LABELS: Record<string, string> = {
  email: "E-post",
  sms: "SMS",
  whatsapp: "WhatsApp",
  none: "Ingen",
};

const STATUS_LABELS: Record<string, string> = {
  sent: "Skickad",
  failed: "Misslyckad",
  skipped: "Hoppade över",
};

function StatusBadge({ status }: { status: string }) {
  if (status === "sent") return <Badge variant="default" className="bg-green-100 text-green-800 border-green-200">{STATUS_LABELS[status] || status}</Badge>;
  if (status === "failed") return <Badge variant="destructive">{STATUS_LABELS[status] || status}</Badge>;
  return <Badge variant="secondary">{STATUS_LABELS[status] || status}</Badge>;
}

function ChannelIcon({ channel }: { channel: string }) {
  if (channel === "email") return <Mail className="h-4 w-4 text-blue-500" />;
  if (channel === "sms") return <MessageSquare className="h-4 w-4 text-green-500" />;
  if (channel === "whatsapp") return <MessageSquare className="h-4 w-4 text-emerald-600" />;
  return <MessageSquare className="h-4 w-4 text-gray-400" />;
}

function formatDateTime(d: Date | string | null | undefined): string {
  if (!d) return "";
  const date = new Date(d);
  return date.toLocaleString("sv-SE", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function CommunicationLogTab() {
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState("");
  const [channelFilter, setChannelFilter] = useState<string>("all");
  const limit = 50;

  const { data, isLoading, refetch } = useQuery<{ logs: CommunicationLog[]; total: number }>({
    queryKey: ["/api/admin/communication/logs", page],
    queryFn: async () => {
      const res = await fetch(`/api/admin/communication/logs?limit=${limit}&offset=${page * limit}`, { credentials: "include" });
      if (!res.ok) throw new Error("Kunde inte hämta logg");
      return res.json();
    },
  });

  const allLogs = data?.logs || [];
  const total = data?.total || 0;

  const filteredLogs = useMemo(() => {
    let logs = allLogs;
    if (channelFilter !== "all") {
      logs = logs.filter(l => l.channel === channelFilter);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      logs = logs.filter(l =>
        (l.recipientName || "").toLowerCase().includes(q) ||
        (l.recipientContact || "").toLowerCase().includes(q) ||
        (l.subject || "").toLowerCase().includes(q)
      );
    }
    return logs;
  }, [allLogs, channelFilter, search]);

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Sök mottagare, ämne..."
            className="pl-8"
            data-testid="input-log-search"
          />
        </div>
        <div className="flex gap-1">
          {(["all", "email", "sms", "whatsapp"] as const).map(ch => (
            <Button
              key={ch}
              variant={channelFilter === ch ? "default" : "outline"}
              size="sm"
              onClick={() => setChannelFilter(ch)}
              data-testid={`filter-channel-${ch}`}
            >
              {ch === "all" ? "Alla" : CHANNEL_LABELS[ch]}
            </Button>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-refresh-logs">
          <RefreshCw className="h-4 w-4 mr-2" />
          Uppdatera
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        {filteredLogs.length !== total
          ? `${filteredLogs.length} av ${total} poster`
          : `${total} poster totalt`}
      </p>

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
        </div>
      ) : filteredLogs.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Mail className="h-12 w-12 mx-auto mb-4 opacity-20" />
            <p>{total === 0 ? "Inga kommunikationsposter än" : "Inga poster matchar sökningen"}</p>
            <p className="text-xs mt-1">{total === 0 ? "Poster skapas automatiskt när följesedlar skickas" : "Prova att ändra sökning eller filter"}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {filteredLogs.map((log) => (
            <Card key={log.id} data-testid={`log-entry-${log.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="mt-0.5">
                    <ChannelIcon channel={log.channel} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900">{log.recipientName || log.recipientContact || "—"}</span>
                      <Badge variant="outline" className="text-xs">{CHANNEL_LABELS[log.channel] || log.channel}</Badge>
                      <StatusBadge status={log.status} />
                    </div>
                    {log.recipientContact && <p className="text-xs text-muted-foreground mt-0.5">{log.recipientContact}</p>}
                    {log.subject && <p className="text-xs text-gray-600 mt-0.5 truncate">{log.subject}</p>}
                    {log.error && <p className="text-xs text-red-600 mt-1">{log.error}</p>}
                    {log.orderId && <p className="text-xs text-muted-foreground mt-0.5">Order-ID: {log.orderId}</p>}
                  </div>
                  <div className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatDateTime(log.createdAt)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
            Föregående
          </Button>
          <span className="text-sm text-muted-foreground">{page + 1} / {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
            Nästa
          </Button>
        </div>
      )}
    </div>
  );
}

interface AttachmentFile {
  name: string;
  size: number;
  type: string;
  base64: string;
}

const DRAFT_KEY_PREFIX = "comm_draft_";

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function EmailComposeTab({ settings }: { settings?: Record<string, string> }) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const draftKey = DRAFT_KEY_PREFIX + "email";

  const [recipientMode, setRecipientMode] = useState<"all" | "select">("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [result, setResult] = useState<{ sent: number; failed: number; total: number } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const { data: clients } = useQuery<Client[]>({ queryKey: ["/api/admin/clients"] });

  const eligibleClients = useMemo(() => (clients || []).filter(c => c.communicationPreference !== "none"), [clients]);
  const targetClients = useMemo(() => {
    if (recipientMode === "all") return eligibleClients;
    return eligibleClients.filter(c => selectedIds.includes(c.id));
  }, [recipientMode, eligibleClients, selectedIds]);
  const missingEmailClients = useMemo(() => targetClients.filter(c => !c.email), [targetClients]);
  const skippedNoneCount = useMemo(() => {
    if (recipientMode === "all") return (clients || []).length - eligibleClients.length;
    return (clients || []).filter(c => selectedIds.includes(c.id) && c.communicationPreference === "none").length;
  }, [clients, eligibleClients, selectedIds, recipientMode]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(draftKey);
      if (saved) {
        const draft = JSON.parse(saved);
        if (draft.subject) setSubject(draft.subject);
        if (draft.body) setBody(draft.body);
      }
    } catch {}
  }, [draftKey]);

  const saveDraft = useCallback(() => {
    try {
      localStorage.setItem(draftKey, JSON.stringify({ subject, body }));
      toast({ title: "Utkast sparat", description: "Ämne och meddelande sparades lokalt." });
    } catch {
      toast({ title: "Kunde inte spara utkast", variant: "destructive" });
    }
  }, [subject, body, draftKey, toast]);

  const clearDraft = () => {
    localStorage.removeItem(draftKey);
    setSubject("");
    setBody("");
    setAttachments([]);
    setSelectedIds([]);
    setRecipientMode("all");
    setResult(null);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const newAttachments: AttachmentFile[] = await Promise.all(
      files.map(async (f) => ({
        name: f.name,
        size: f.size,
        type: f.type,
        base64: await readFileAsBase64(f),
      }))
    );
    setAttachments(prev => [...prev, ...newAttachments]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const recipientCount = targetClients.length;
  const canSend = recipientCount > 0 && !!body && !!subject;

  const sendMutation = useMutation({
    mutationFn: () => {
      const recipientIds = targetClients.map(c => c.id);
      return apiRequest("POST", "/api/admin/communication/email", {
        recipientIds,
        subject,
        body,
        attachments: attachments.map(a => ({ filename: a.name, base64: a.base64, contentType: a.type })),
      });
    },
    onSuccess: (data: any) => {
      setResult(data);
      setShowConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/communication/logs"] });
      toast({ title: `Kampanj skickad: ${data.sent} lyckades, ${data.failed} misslyckades` });
    },
    onError: (err: any) => {
      setShowConfirm(false);
      toast({ title: "Fel", description: err?.message || "Kunde inte skicka kampanj.", variant: "destructive" });
    },
  });

  const toggleClient = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSendClick = () => {
    if (recipientCount > 1) setShowConfirm(true);
    else sendMutation.mutate();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nytt e-postutskick</CardTitle>
          <CardDescription>Skicka e-post till valda kunder</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <label className="text-sm font-medium">Mottagare</label>
            <div className="flex gap-2 flex-wrap">
              <Button variant={recipientMode === "all" ? "default" : "outline"} size="sm" onClick={() => setRecipientMode("all")} data-testid="button-recipients-all">
                Alla ({eligibleClients.length} kunder med kommunikation aktiverad)
              </Button>
              <Button variant={recipientMode === "select" ? "default" : "outline"} size="sm" onClick={() => setRecipientMode("select")} data-testid="button-recipients-select">
                Välj kunder
              </Button>
            </div>

            {recipientMode === "select" && (
              <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1 mt-2">
                {(clients || []).map(c => {
                  const isNone = c.communicationPreference === "none";
                  const missingEmail = !c.email;
                  return (
                    <label key={c.id} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50 ${isNone ? "opacity-50" : ""}`}>
                      <Checkbox checked={selectedIds.includes(c.id)} onCheckedChange={() => !isNone && toggleClient(c.id)} disabled={isNone} data-testid={`checkbox-client-${c.id}`} />
                      <span className="text-sm flex-1">{c.name}</span>
                      {isNone && <Badge variant="outline" className="text-xs">Ingen kommunikation</Badge>}
                      {!isNone && missingEmail && <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">Saknar e-post</Badge>}
                      {!isNone && !missingEmail && <span className="text-xs text-muted-foreground">{c.email}</span>}
                    </label>
                  );
                })}
              </div>
            )}

            {skippedNoneCount > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="warning-none-skipped">
                {skippedNoneCount} kund{skippedNoneCount !== 1 ? "er" : ""} med "Ingen kommunikation" hoppas automatiskt över.
              </p>
            )}
            {missingEmailClients.length > 0 && (
              <p className="text-xs text-amber-600" data-testid="warning-missing-contact">
                {missingEmailClients.length} kund{missingEmailClients.length !== 1 ? "er" : ""} saknar e-postadress och hoppas över.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Ämne</label>
            <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Nyhetsbrev från bageri" data-testid="input-campaign-subject" />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Meddelande</label>
            <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Skriv ditt meddelande här..." rows={6} data-testid="textarea-campaign-body" />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Bilagor</label>
              <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="button-add-attachment">
                <Paperclip className="h-4 w-4 mr-1.5" />
                Lägg till bilaga
              </Button>
            </div>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange} data-testid="input-file-attachment" />
            {attachments.length > 0 ? (
              <div className="space-y-1.5">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-md bg-gray-50 border text-sm" data-testid={`attachment-item-${i}`}>
                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{a.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatFileSize(a.size)}</span>
                    <button onClick={() => removeAttachment(i)} className="text-muted-foreground hover:text-red-500 transition-colors" data-testid={`button-remove-attachment-${i}`}>
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Inga bilagor tillagda. Bilagor skickas med till alla mottagare.</p>
            )}
          </div>

          <div className="flex items-center justify-between pt-2 border-t flex-wrap gap-2">
            <p className="text-sm text-muted-foreground">
              <Users className="h-4 w-4 inline mr-1" />
              {recipientCount} mottagare valda
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={saveDraft} disabled={!subject && !body} data-testid="button-save-draft">
                <Save className="h-4 w-4 mr-1.5" />
                Spara utkast
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowPreview(true)} disabled={!subject && !body} data-testid="button-preview-email">
                <Eye className="h-4 w-4 mr-1.5" />
                Förhandsgranskning
              </Button>
              <Button onClick={handleSendClick} disabled={sendMutation.isPending || !canSend} data-testid="button-send-campaign">
                <Send className="h-4 w-4 mr-2" />
                {sendMutation.isPending ? "Skickar..." : `Skicka till ${recipientCount} mottagare`}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-2xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Förhandsgranskning av e-post</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 border rounded-lg p-4 bg-white">
            <div className="flex items-start gap-2 border-b pb-2">
              <span className="text-xs text-muted-foreground w-16 shrink-0 pt-0.5">Från:</span>
              <span className="text-sm">{settings?.smtp_from_name || "Rätt Order"} &lt;{settings?.smtp_from_email || "noreply@example.com"}&gt;</span>
            </div>
            <div className="flex items-start gap-2 border-b pb-2">
              <span className="text-xs text-muted-foreground w-16 shrink-0 pt-0.5">Ämne:</span>
              <span className="text-sm font-medium">{subject || "(inget ämne)"}</span>
            </div>
            <div className="flex items-start gap-2 border-b pb-2">
              <span className="text-xs text-muted-foreground w-16 shrink-0 pt-0.5">Till:</span>
              <span className="text-sm text-muted-foreground">{recipientCount} mottagare</span>
            </div>
            {attachments.length > 0 && (
              <div className="flex items-start gap-2 border-b pb-2">
                <span className="text-xs text-muted-foreground w-16 shrink-0 pt-0.5">Bilagor:</span>
                <div className="text-sm">{attachments.map(a => a.name).join(", ")}</div>
              </div>
            )}
            <div className="pt-1 whitespace-pre-wrap text-sm text-gray-800 min-h-[100px]">{body || "(inget meddelande)"}</div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Stäng</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-sm w-full" data-testid="confirm-send-dialog">
            <CardHeader>
              <CardTitle>Bekräfta utskick</CardTitle>
              <CardDescription>
                Du är på väg att skicka ett e-postmeddelande till <strong>{recipientCount} mottagare</strong>.
                {attachments.length > 0 && ` Meddelandet innehåller ${attachments.length} bilaga${attachments.length !== 1 ? "r" : ""}.`}
                {missingEmailClients.length > 0 && ` ${missingEmailClients.length} kunder saknar e-post och hoppas över.`}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="button-confirm-cancel">Avbryt</Button>
              <Button onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending} data-testid="button-confirm-send">
                {sendMutation.isPending ? "Skickar..." : "Bekräfta och skicka"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {result && (
        <Card data-testid="campaign-result">
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 justify-between flex-wrap">
              <div className="flex gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{result.sent}</p>
                  <p className="text-xs text-muted-foreground">Skickade</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-red-500">{result.failed}</p>
                  <p className="text-xs text-muted-foreground">Misslyckade</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-700">{result.total}</p>
                  <p className="text-xs text-muted-foreground">Totalt</p>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={clearDraft} data-testid="button-new-campaign">Nytt utskick</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SmsComposeTab() {
  const { toast } = useToast();
  const [recipientMode, setRecipientMode] = useState<"all" | "select">("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [body, setBody] = useState("");
  const [result, setResult] = useState<{ sent: number; failed: number; total: number } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const { data: clients } = useQuery<Client[]>({ queryKey: ["/api/admin/clients"] });

  const eligibleClients = useMemo(() => (clients || []).filter(c => c.communicationPreference !== "none"), [clients]);
  const targetClients = useMemo(() => {
    if (recipientMode === "all") return eligibleClients;
    return eligibleClients.filter(c => selectedIds.includes(c.id));
  }, [recipientMode, eligibleClients, selectedIds]);
  const missingPhoneClients = useMemo(() => targetClients.filter(c => !c.phone), [targetClients]);
  const skippedNoneCount = useMemo(() => {
    if (recipientMode === "all") return (clients || []).length - eligibleClients.length;
    return (clients || []).filter(c => selectedIds.includes(c.id) && c.communicationPreference === "none").length;
  }, [clients, eligibleClients, selectedIds, recipientMode]);

  const sendMutation = useMutation({
    mutationFn: () => {
      const recipientIds = targetClients.map(c => c.id);
      return apiRequest("POST", "/api/admin/communication/sms", { recipientIds, message: body });
    },
    onSuccess: (data: any) => {
      setResult(data);
      setShowConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/communication/logs"] });
      toast({ title: `SMS-kampanj skickad: ${data.sent} lyckades, ${data.failed} misslyckades` });
    },
    onError: (err: any) => {
      setShowConfirm(false);
      toast({ title: "Fel", description: err?.message || "Kunde inte skicka kampanj.", variant: "destructive" });
    },
  });

  const toggleClient = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const recipientCount = targetClients.length;
  const canSend = recipientCount > 0 && !!body;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nytt SMS-utskick</CardTitle>
          <CardDescription>Skicka SMS via 46elks till valda kunder</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <label className="text-sm font-medium">Mottagare</label>
            <div className="flex gap-2 flex-wrap">
              <Button variant={recipientMode === "all" ? "default" : "outline"} size="sm" onClick={() => setRecipientMode("all")} data-testid="button-recipients-all">
                Alla ({eligibleClients.length} kunder med kommunikation aktiverad)
              </Button>
              <Button variant={recipientMode === "select" ? "default" : "outline"} size="sm" onClick={() => setRecipientMode("select")} data-testid="button-recipients-select">
                Välj kunder
              </Button>
            </div>

            {recipientMode === "select" && (
              <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1 mt-2">
                {(clients || []).map(c => {
                  const isNone = c.communicationPreference === "none";
                  const missingPhone = !c.phone;
                  return (
                    <label key={c.id} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50 ${isNone ? "opacity-50" : ""}`}>
                      <Checkbox checked={selectedIds.includes(c.id)} onCheckedChange={() => !isNone && toggleClient(c.id)} disabled={isNone} data-testid={`checkbox-client-${c.id}`} />
                      <span className="text-sm flex-1">{c.name}</span>
                      {isNone && <Badge variant="outline" className="text-xs">Ingen kommunikation</Badge>}
                      {!isNone && missingPhone && <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">Saknar telefon</Badge>}
                      {!isNone && !missingPhone && <span className="text-xs text-muted-foreground">{c.phone}</span>}
                    </label>
                  );
                })}
              </div>
            )}

            {skippedNoneCount > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="warning-none-skipped">
                {skippedNoneCount} kund{skippedNoneCount !== 1 ? "er" : ""} med "Ingen kommunikation" hoppas automatiskt över.
              </p>
            )}
            {missingPhoneClients.length > 0 && (
              <p className="text-xs text-amber-600" data-testid="warning-missing-contact">
                {missingPhoneClients.length} kund{missingPhoneClients.length !== 1 ? "er" : ""} saknar telefonnummer och hoppas över.
              </p>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Meddelande</label>
            <Textarea value={body} onChange={e => setBody(e.target.value)} placeholder="Skriv ditt SMS-meddelande här..." rows={4} data-testid="textarea-campaign-message" />
            <p className="text-xs text-muted-foreground">{body.length} tecken · ~{Math.ceil(body.length / 160) || 1} SMS-segment per mottagare</p>
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              <Users className="h-4 w-4 inline mr-1" />
              {recipientCount} mottagare valda
            </p>
            <Button onClick={() => recipientCount > 1 ? setShowConfirm(true) : sendMutation.mutate()} disabled={sendMutation.isPending || !canSend} data-testid="button-send-campaign">
              <Send className="h-4 w-4 mr-2" />
              {sendMutation.isPending ? "Skickar..." : `Skicka till ${recipientCount} mottagare`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-sm w-full" data-testid="confirm-send-dialog">
            <CardHeader>
              <CardTitle>Bekräfta utskick</CardTitle>
              <CardDescription>
                Du är på väg att skicka ett SMS till <strong>{recipientCount} mottagare</strong>.
                {missingPhoneClients.length > 0 && ` ${missingPhoneClients.length} kunder saknar telefonnummer och hoppas över.`}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="button-confirm-cancel">Avbryt</Button>
              <Button onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending} data-testid="button-confirm-send">
                {sendMutation.isPending ? "Skickar..." : "Bekräfta och skicka"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {result && (
        <Card data-testid="campaign-result">
          <CardContent className="pt-5">
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{result.sent}</p>
                <p className="text-xs text-muted-foreground">Skickade</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-500">{result.failed}</p>
                <p className="text-xs text-muted-foreground">Misslyckade</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-700">{result.total}</p>
                <p className="text-xs text-muted-foreground">Totalt</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function WhatsAppComposeTab({ settings }: { settings?: Record<string, string> }) {
  const { toast } = useToast();
  const [recipientMode, setRecipientMode] = useState<"all" | "select">("all");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [templateName, setTemplateName] = useState("");
  const [languageCode, setLanguageCode] = useState("");
  const [variables, setVariables] = useState<string[]>([""]);
  const [result, setResult] = useState<{ sent: number; failed: number; total: number } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    if (settings?.whatsapp_template_name) setTemplateName(settings.whatsapp_template_name);
    if (settings?.whatsapp_language_code) setLanguageCode(settings.whatsapp_language_code);
  }, [settings]);

  const { data: clients } = useQuery<Client[]>({ queryKey: ["/api/admin/clients"] });

  const eligibleClients = useMemo(() => (clients || []).filter(c => c.communicationPreference !== "none"), [clients]);
  const targetClients = useMemo(() => {
    if (recipientMode === "all") return eligibleClients;
    return eligibleClients.filter(c => selectedIds.includes(c.id));
  }, [recipientMode, eligibleClients, selectedIds]);
  const missingPhoneClients = useMemo(() => targetClients.filter(c => !c.phone), [targetClients]);
  const skippedNoneCount = useMemo(() => {
    if (recipientMode === "all") return (clients || []).length - eligibleClients.length;
    return (clients || []).filter(c => selectedIds.includes(c.id) && c.communicationPreference === "none").length;
  }, [clients, eligibleClients, selectedIds, recipientMode]);

  const addVariable = () => setVariables(prev => [...prev, ""]);
  const removeVariable = (i: number) => setVariables(prev => prev.filter((_, idx) => idx !== i));
  const updateVariable = (i: number, value: string) => setVariables(prev => prev.map((v, idx) => idx === i ? value : v));

  const sendMutation = useMutation({
    mutationFn: () => {
      const recipientIds = targetClients.map(c => c.id);
      return apiRequest("POST", "/api/admin/communication/whatsapp", {
        recipientIds,
        message: variables[0] || "",
        templateName: templateName || undefined,
        languageCode: languageCode || undefined,
        templateParams: variables,
      });
    },
    onSuccess: (data: any) => {
      setResult(data);
      setShowConfirm(false);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/communication/logs"] });
      toast({ title: `WhatsApp-kampanj skickad: ${data.sent} lyckades, ${data.failed} misslyckades` });
    },
    onError: (err: any) => {
      setShowConfirm(false);
      toast({ title: "Fel", description: err?.message || "Kunde inte skicka kampanj.", variant: "destructive" });
    },
  });

  const toggleClient = (id: string) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const recipientCount = targetClients.length;
  const canSend = recipientCount > 0 && !!templateName && variables.some(v => v.trim());

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Nytt WhatsApp-utskick</CardTitle>
          <CardDescription>Skicka WhatsApp-meddelande via Meta Cloud API till valda kunder</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="space-y-2">
            <label className="text-sm font-medium">Mottagare</label>
            <div className="flex gap-2 flex-wrap">
              <Button variant={recipientMode === "all" ? "default" : "outline"} size="sm" onClick={() => setRecipientMode("all")} data-testid="button-recipients-all">
                Alla ({eligibleClients.length} kunder med kommunikation aktiverad)
              </Button>
              <Button variant={recipientMode === "select" ? "default" : "outline"} size="sm" onClick={() => setRecipientMode("select")} data-testid="button-recipients-select">
                Välj kunder
              </Button>
            </div>

            {recipientMode === "select" && (
              <div className="border rounded-lg max-h-48 overflow-y-auto p-2 space-y-1 mt-2">
                {(clients || []).map(c => {
                  const isNone = c.communicationPreference === "none";
                  const missingPhone = !c.phone;
                  return (
                    <label key={c.id} className={`flex items-center gap-2 cursor-pointer p-1.5 rounded hover:bg-gray-50 ${isNone ? "opacity-50" : ""}`}>
                      <Checkbox checked={selectedIds.includes(c.id)} onCheckedChange={() => !isNone && toggleClient(c.id)} disabled={isNone} data-testid={`checkbox-client-${c.id}`} />
                      <span className="text-sm flex-1">{c.name}</span>
                      {isNone && <Badge variant="outline" className="text-xs">Ingen kommunikation</Badge>}
                      {!isNone && missingPhone && <Badge variant="outline" className="text-xs text-amber-600 border-amber-400">Saknar telefon</Badge>}
                      {!isNone && !missingPhone && <span className="text-xs text-muted-foreground">{c.phone}</span>}
                    </label>
                  );
                })}
              </div>
            )}

            {skippedNoneCount > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="warning-none-skipped">
                {skippedNoneCount} kund{skippedNoneCount !== 1 ? "er" : ""} med "Ingen kommunikation" hoppas automatiskt över.
              </p>
            )}
            {missingPhoneClients.length > 0 && (
              <p className="text-xs text-amber-600" data-testid="warning-missing-contact">
                {missingPhoneClients.length} kund{missingPhoneClients.length !== 1 ? "er" : ""} saknar telefonnummer och hoppas över.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-sm font-medium">Mallnamn</label>
              <Input
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
                placeholder="t.ex. delivery_note"
                data-testid="input-wa-template-name"
              />
              <p className="text-xs text-muted-foreground">Mallens exakta namn i Meta Business Manager</p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Språkkod</label>
              <Input
                value={languageCode}
                onChange={e => setLanguageCode(e.target.value)}
                placeholder="sv"
                data-testid="input-wa-language-code"
              />
              <p className="text-xs text-muted-foreground">t.ex. sv, en_US, fi</p>
            </div>
          </div>

          {(settings?.whatsapp_template_name || settings?.whatsapp_language_code) && (
            <div className="flex items-center gap-2 p-2 rounded-md bg-emerald-50 border border-emerald-200">
              <MessageSquare className="h-4 w-4 text-emerald-600 shrink-0" />
              <p className="text-xs text-emerald-800">
                Standardmall från inställningar: <strong>{settings?.whatsapp_template_name}</strong>
                {settings?.whatsapp_language_code && ` (${settings.whatsapp_language_code})`}
              </p>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">Mallvariabler</label>
              <Button variant="outline" size="sm" onClick={addVariable} data-testid="button-add-variable">
                <Plus className="h-4 w-4 mr-1.5" />
                Lägg till variabel
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Fyll i värden för mallens variabler &#123;&#123;1&#125;&#125;, &#123;&#123;2&#125;&#125; osv. i ordning.
            </p>
            <div className="space-y-2">
              {variables.map((v, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`variable-row-${i}`}>
                  <span className="text-xs text-muted-foreground w-8 shrink-0 text-right font-mono">{`{{${i + 1}}}`}</span>
                  <Input
                    value={v}
                    onChange={e => updateVariable(i, e.target.value)}
                    placeholder={`Variabel ${i + 1}`}
                    data-testid={`input-wa-variable-${i}`}
                  />
                  {variables.length > 1 && (
                    <button onClick={() => removeVariable(i)} className="text-muted-foreground hover:text-red-500 transition-colors" data-testid={`button-remove-variable-${i}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t">
            <p className="text-sm text-muted-foreground">
              <Users className="h-4 w-4 inline mr-1" />
              {recipientCount} mottagare valda
            </p>
            <Button onClick={() => recipientCount > 1 ? setShowConfirm(true) : sendMutation.mutate()} disabled={sendMutation.isPending || !canSend} data-testid="button-send-campaign">
              <Send className="h-4 w-4 mr-2" />
              {sendMutation.isPending ? "Skickar..." : `Skicka till ${recipientCount} mottagare`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="max-w-sm w-full" data-testid="confirm-send-dialog">
            <CardHeader>
              <CardTitle>Bekräfta utskick</CardTitle>
              <CardDescription>
                Du är på väg att skicka ett WhatsApp-meddelande till <strong>{recipientCount} mottagare</strong> med mallen <strong>{templateName}</strong>.
                {missingPhoneClients.length > 0 && ` ${missingPhoneClients.length} kunder saknar telefonnummer och hoppas över.`}
              </CardDescription>
            </CardHeader>
            <CardContent className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowConfirm(false)} data-testid="button-confirm-cancel">Avbryt</Button>
              <Button onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending} data-testid="button-confirm-send">
                {sendMutation.isPending ? "Skickar..." : "Bekräfta och skicka"}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {result && (
        <Card data-testid="campaign-result">
          <CardContent className="pt-5">
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">{result.sent}</p>
                <p className="text-xs text-muted-foreground">Skickade</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-500">{result.failed}</p>
                <p className="text-xs text-muted-foreground">Misslyckade</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-700">{result.total}</p>
                <p className="text-xs text-muted-foreground">Totalt</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function AdminCommunicationPage() {
  const { data: settings } = useQuery<Record<string, string>>({
    queryKey: ["/api/admin/settings"],
  });

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Kommunikation</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Skicka e-post, SMS och WhatsApp-meddelanden till kunder samt visa kommunikationslogg
        </p>
      </div>

      <Tabs defaultValue="email">
        <TabsList data-testid="communication-tabs">
          <TabsTrigger value="email" data-testid="tab-email">
            <Mail className="h-4 w-4 mr-1.5" />
            E-post
          </TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms">
            <MessageSquare className="h-4 w-4 mr-1.5" />
            SMS
          </TabsTrigger>
          <TabsTrigger value="whatsapp" data-testid="tab-whatsapp">
            <MessageSquare className="h-4 w-4 mr-1.5" />
            WhatsApp
          </TabsTrigger>
          <TabsTrigger value="log" data-testid="tab-log">
            Historik
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="mt-6">
          <EmailComposeTab settings={settings} />
        </TabsContent>

        <TabsContent value="sms" className="mt-6">
          <SmsComposeTab />
        </TabsContent>

        <TabsContent value="whatsapp" className="mt-6">
          <WhatsAppComposeTab settings={settings} />
        </TabsContent>

        <TabsContent value="log" className="mt-6">
          <CommunicationLogTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
